import { z } from 'zod/v4';
import { CompanyDocumentSchema } from './company-document.js';

/**
 * Sort keys the admin list accepts. The legacy schema also advertised
 * `facilityProcesses`, `internalCompanyCode` and `notes`, but their ORDER BY
 * pointed at a column and a `cp_doc` alias this query never joined — every
 * request that used one failed with a 500 — so they are not carried over.
 */
export const ADMIN_COMPANY_DOCUMENT_LIST_SORT_COLUMNS = [
  'id',
  'name',
  'type',
  'companyName',
  'createdBy',
  'updatedAt',
] as const;

export type AdminCompanyDocumentListSortColumn =
  (typeof ADMIN_COMPANY_DOCUMENT_LIST_SORT_COLUMNS)[number];

const sortColumns = ADMIN_COMPANY_DOCUMENT_LIST_SORT_COLUMNS.join('|');
// `col:DIR`, comma-separated for a multi-column sort. Direction is upper-case
// only, as the legacy pattern was.
const SORT_PATTERN = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

/**
 * The old dashboard also sends a top-level `type` (always empty) and legacy
 * accepted `q`, which no screen sends; both are dropped like any unknown param.
 */
export const AdminCompanyDocumentsListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(SORT_PATTERN).optional(),
  /**
   * JSON object of column filters: `name`, `companyName` (case-insensitive
   * substring), `id`, `companyId` (exact) and `type` (document type ids,
   * comma-separated). Malformed JSON and other keys are ignored, not rejected.
   */
  filter: z.string().max(512).optional(),
});

export type AdminCompanyDocumentsListQuery = z.infer<
  typeof AdminCompanyDocumentsListQuerySchema
>;

/**
 * One document as the admin list returns it — every company's, which is the
 * point of the view. Legacy also sent the remaining audit columns (impersonator,
 * API key, creating company); no screen read them, so they are left out.
 */
export const AdminCompanyDocumentListItemSchema = CompanyDocumentSchema.extend({
  // Nullable on the table; the client lists never met a nameless row.
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  /** `first_name || ' ' || last_name` of the uploader. */
  createdBy: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
});

export type AdminCompanyDocumentListItem = z.infer<
  typeof AdminCompanyDocumentListItemSchema
>;

// The platform's paginated envelope. `pagination.page` is 1-based, unlike the
// 0-based `page` query param. Legacy sent `{ data, meta: { count, page,
// pageSize, totalCount } }`.
export const AdminCompanyDocumentsListResponseSchema = z.object({
  metadata: z.object({
    success: z.boolean(),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
  data: z.array(AdminCompanyDocumentListItemSchema),
});

export type AdminCompanyDocumentsListResponse = z.infer<
  typeof AdminCompanyDocumentsListResponseSchema
>;

/**
 * One row of the archived admin list: the document plus the company whose
 * archive row this is. Archiving is per company, so a document archived by two
 * companies comes back twice, differing only here; a restore names it.
 */
export const AdminArchivedCompanyDocumentListItemSchema =
  AdminCompanyDocumentListItemSchema.extend({
    archivedByCompanyId: z.string(),
  });

export type AdminArchivedCompanyDocumentListItem = z.infer<
  typeof AdminArchivedCompanyDocumentListItemSchema
>;

// Same envelope as the active list. Legacy sent `{ data, meta }`.
export const AdminArchivedCompanyDocumentsListResponseSchema =
  AdminCompanyDocumentsListResponseSchema.extend({
    data: z.array(AdminArchivedCompanyDocumentListItemSchema),
  });

export type AdminArchivedCompanyDocumentsListResponse = z.infer<
  typeof AdminArchivedCompanyDocumentsListResponseSchema
>;
